const API_BASE = '/api/masters';

// Absolute base for window.open()-driven downloads (CRA's dev "proxy" option only
// forwards fetch/XHR, not full-page navigations, so localhost dev needs an absolute
// URL). In production REACT_APP_API_URL is normally unset and the backend shares the
// frontend's origin, so we must fall back to a same-origin relative path there,
// never to localhost.
const isLocalhostHost =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const API_ABSOLUTE_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/masters`
  : isLocalhostHost
    ? 'http://localhost:5000/api/masters'
    : '/api/masters';

let cachedCompactCourses = null;
let cachedCompactCoursesPromise = null;

// Error with the server's { message } when there is one (e.g. "You don't
// have permission to edit here…"), otherwise the raw response text.
async function errorFromResponse(res) {
  const text = await res.text();
  let message = text;
  try { message = JSON.parse(text)?.message || text; } catch (_) {}
  return new Error(message);
}

async function api(path, { method = 'GET', body, headers } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(headers || {}) },
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

export const MastersService = {
  // Helper: build template URL for any resource (absolute to avoid dev proxy on HTML requests)
  getTemplateUrl: (resource) => `${API_ABSOLUTE_BASE}/${resource}/template`,
  // Counts for master resources
  getCounts: () => api('/counts'),
  // Locations bundle (countries + states + cities + counts)
  getLocations: () => api('/locations'),
  // Countries
  listCountries: () => api('/countries'),
  createCountry: (data) => api('/countries', { method: 'POST', body: data }),
  updateCountry: (id, data) => api(`/countries/${id}`, { method: 'PUT', body: data }),
  deleteCountry: (id) => api(`/countries/${id}`, { method: 'DELETE' }),
  downloadCountriesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/countries/template`, '_blank'),
  uploadCountries: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/countries/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // States
  listStates: (country_id) => api(`/states${country_id ? `?country_id=${country_id}` : ''}`),
  createState: (data) => api('/states', { method: 'POST', body: data }),
  updateState: (id, data) => api(`/states/${id}`, { method: 'PUT', body: data }),
  deleteState: (id) => api(`/states/${id}`, { method: 'DELETE' }),
  downloadStatesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/states/template`, '_blank'),
  uploadStates: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/states/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Cities
  listCities: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.country_id) params.append('country_id', filters.country_id);
    if (filters.state_id) params.append('state_id', filters.state_id);
    const qs = params.toString();
    return api(`/cities${qs ? `?${qs}` : ''}`);
  },
  createCity: (data) => api('/cities', { method: 'POST', body: data }),
  updateCity: (id, data) => api(`/cities/${id}`, { method: 'PUT', body: data }),
  deleteCity: (id) => api(`/cities/${id}`, { method: 'DELETE' }),
  downloadCitiesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/cities/template`, '_blank'),
  uploadCities: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/cities/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Institution Types
  listInstitutionTypes: () => api('/institution-types'),
  createInstitutionType: (data) => api('/institution-types', { method: 'POST', body: data }),
  updateInstitutionType: (id, data) => api(`/institution-types/${id}`, { method: 'PUT', body: data }),
  deleteInstitutionType: (id) => api(`/institution-types/${id}`, { method: 'DELETE' }),
  downloadInstitutionTypesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/institution-types/template`, '_blank'),
  uploadInstitutionTypes: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/institution-types/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Document Types
  listDocumentTypes: (status) => api(`/document-types${status ? `?status=${status}` : ''}`),
  createDocumentType: (data) => api('/document-types', { method: 'POST', body: data }),
  updateDocumentType: (id, data) => api(`/document-types/${id}`, { method: 'PUT', body: data }),
  deleteDocumentType: (id) => api(`/document-types/${id}`, { method: 'DELETE' }),

  // Institutions
  listInstitutions: (filters = {}) => {
    const params = new URLSearchParams();
    for (const k of ['country_id', 'state_id', 'city_id', 'type_id', 'rating']) {
      if (filters[k]) params.append(k, filters[k]);
    }
    const qs = params.toString();
    return api(`/institutions${qs ? `?${qs}` : ''}`);
  },
  createInstitution: (data) => api('/institutions', { method: 'POST', body: data }),
  updateInstitution: (id, data) => api(`/institutions/${id}`, { method: 'PUT', body: data }),
  deleteInstitution: (id) => api(`/institutions/${id}`, { method: 'DELETE' }),
  downloadInstitutionsTemplate: () => window.open(`${API_ABSOLUTE_BASE}/institutions/template`, '_blank'),
  uploadInstitutions: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/institutions/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Representatives
  listRepresentatives: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    if (params.company_id) qs.append('company_id', params.company_id);
    const query = qs.toString();
    return api(`/representatives${query ? `?${query}` : ''}`);
  },
  createRepresentative: (data) => api('/representatives', { method: 'POST', body: data }),
  updateRepresentative: (id, data) => api(`/representatives/${id}`, { method: 'PUT', body: data }),
  deleteRepresentative: (id) => api(`/representatives/${id}`, { method: 'DELETE' }),

  // Companies
  listCompanies: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.page) qs.append('page', params.page);
    if (params.limit) qs.append('limit', params.limit);
    if (params.search) qs.append('search', params.search);
    const query = qs.toString();
    return api(`/companies${query ? `?${query}` : ''}`);
  },
  createCompany: (data) => api('/companies', { method: 'POST', body: data }),
  updateCompany: (id, data) => api(`/companies/${id}`, { method: 'PUT', body: data }),
  deleteCompany: (id) => api(`/companies/${id}`, { method: 'DELETE' }),
  revealCompanyPassword: (id) => api(`/companies/${id}/reveal-password`),
  getInstitutionDirectory: (institutionId) => api(`/institutions/${institutionId}/directory`),

  // Institutions: gallery upload
  uploadInstitutionGallery: async (institution_id, files = [], opts = {}) => {
    const formData = new FormData();
    for (const f of files) formData.append('images', f);
    const params = new URLSearchParams();
    if (opts.replace) params.append('replace', 'true');
    const url = `${API_BASE}/institutions/${institution_id}/gallery${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  deleteInstitutionGalleryImage: async (institution_id, index) => {
    const res = await fetch(`${API_BASE}/institutions/${institution_id}/gallery/${index}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Countries: Senior Counsellor photo
  uploadCountryCounsellorPhoto: async (country_id, file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const res = await fetch(`${API_BASE}/countries/${country_id}/counsellor-photo`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  deleteCountryCounsellorPhoto: async (country_id) => {
    const res = await fetch(`${API_BASE}/countries/${country_id}/counsellor-photo`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Institutions: logo upload
  uploadInstitutionLogo: async (institution_id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const url = `${API_BASE}/institutions/${institution_id}/logo`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  deleteInstitutionLogo: async (institution_id) => {
    const res = await fetch(`${API_BASE}/institutions/${institution_id}/logo`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  uploadInstitutionDocument: async (institution_id, file) => {
    const formData = new FormData();
    formData.append('document', file);
    const url = `${API_BASE}/institutions/${institution_id}/document`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Campuses
  listCampuses: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.institution_id) params.append('institution_id', filters.institution_id);
    const qs = params.toString();
    return api(`/campuses${qs ? `?${qs}` : ''}`);
  },
  createCampus: (data) => api('/campuses', { method: 'POST', body: data }),
  updateCampus: (id, data) => api(`/campuses/${id}`, { method: 'PUT', body: data }),
  deleteCampus: (id) => api(`/campuses/${id}`, { method: 'DELETE' }),
  downloadCampusesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/campuses/template`, '_blank'),
  uploadCampuses: async (file, opts = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (opts.overwriteExisting) params.append('overwriteExisting', 'true');
    const url = `${API_BASE}/campuses/upload${params.toString() ? `?${params}` : ''}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  downloadCampusesExcel: () => window.open(`${API_ABSOLUTE_BASE}/campuses/export`, '_blank'),

  // Edu Grades
  listEduGrades: () => api('/edu-grades'),
  createEduGrade: (data) => api('/edu-grades', { method: 'POST', body: data }),
  updateEduGrade: (id, data) => api(`/edu-grades/${id}`, { method: 'PUT', body: data }),
  deleteEduGrade: (id) => api(`/edu-grades/${id}`, { method: 'DELETE' }),
  downloadEduGradesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/edu-grades/template`, '_blank'),
  uploadEduGrades: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/edu-grades/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  listStreams: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.field_of_study) params.append('field_of_study', filters.field_of_study);
    const qs = params.toString();
    return api(`/streams${qs ? `?${qs}` : ''}`);
  },
  createStream: (data) => api('/streams', { method: 'POST', body: data }),
  updateStream: (id, data) => api(`/streams/${id}`, { method: 'PUT', body: data }),
  deleteStream: (id) => api(`/streams/${id}`, { method: 'DELETE' }),
  downloadStreamsTemplate: () => window.open(`${API_ABSOLUTE_BASE}/streams/template`, '_blank'),
  uploadStreams: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/streams/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Areas
  listAreas: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.stream_id) params.append('stream_id', filters.stream_id);
    const qs = params.toString();
    return api(`/areas${qs ? `?${qs}` : ''}`);
  },
  createArea: (data) => api('/areas', { method: 'POST', body: data }),
  updateArea: (id, data) => api(`/areas/${id}`, { method: 'PUT', body: data }),
  deleteArea: (id) => api(`/areas/${id}`, { method: 'DELETE' }),
  downloadAreasTemplate: () => window.open(`${API_ABSOLUTE_BASE}/areas/template`, '_blank'),
  uploadAreas: async (file, stream_id) => {
    const formData = new FormData();
    formData.append('file', file);
    const url = `${API_BASE}/areas/upload${stream_id ? `?stream_id=${encodeURIComponent(stream_id)}` : ''}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Levels
  listLevels: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.country_id) params.append('country_id', filters.country_id);
    const qs = params.toString();
    return api(`/levels${qs ? `?${qs}` : ''}`);
  },
  createLevel: (data) => api('/levels', { method: 'POST', body: data }),
  updateLevel: (id, data) => api(`/levels/${id}`, { method: 'PUT', body: data }),
  deleteLevel: (id) => api(`/levels/${id}`, { method: 'DELETE' }),
  downloadLevelsTemplate: () => window.open(`${API_ABSOLUTE_BASE}/levels/template`, '_blank'),
  uploadLevels: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_BASE}/levels/upload`, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },

  // Courses (Admissions)
  listCourses: (filters = {}) => {
    const params = new URLSearchParams();
    for (const k of ['institution_id', 'country_id', 'state_id', 'campus_id', 'stream_id', 'area_id', 'level_id', 'search', 'page', 'limit', 'compact', 'sort']) {
      if (filters[k]) params.append(k, filters[k]);
    }
    const qs = params.toString();
    return api(`/courses${qs ? `?${qs}` : ''}`);
  },
  getTopInstitutionsByCountry: (perCountry = 6) => api(`/courses/top-institutions-by-country?per_country=${perCountry}`),
  getCourse: (id) => api(`/courses/${id}`),
  createCourse: (data) => api('/courses', { method: 'POST', body: data }),
  updateCourse: (id, data) => api(`/courses/${id}`, { method: 'PUT', body: data }),
  deleteCourse: (id, opts = {}) => {
    const qs = opts.hard ? '?hard=true' : '';
    return api(`/courses/${id}${qs}`, { method: 'DELETE' });
  },
  deleteAllCourses: (filters = {}, opts = {}) => {
    const params = new URLSearchParams();
    if (opts.hard) params.append('hard', 'true');
    for (const k of ['institution_id', 'country_id', 'state_id', 'campus_id', 'stream_id', 'area_id', 'level_id', 'search']) {
      if (filters[k]) params.append(k, filters[k]);
    }
    const qs = params.toString();
    return api(`/courses/all${qs ? `?${qs}` : ''}`, { method: 'DELETE' });
  },
  downloadCoursesTemplate: () => window.open(`${API_ABSOLUTE_BASE}/courses/template`, '_blank'),
  downloadCoursesSmartTemplate: () => window.open(`${API_ABSOLUTE_BASE}/courses/smart-template`, '_blank'),
  downloadCoursesBulkUpdateTemplate: async (ids = [], columns = [], opts = {}) => {
    const res = await fetch(`${API_BASE}/courses/bulk-update-template`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ids,
        columns,
        selectAll: opts.selectAll,
        filters: opts.filters,
        search: opts.search,
      }),
      credentials: 'include',
    });
    if (!res.ok) throw await errorFromResponse(res);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'courses_bulk_update_template.xlsx';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
  uploadCourses: async (file, opts = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (opts.dryRun) params.append('dry_run', 'true');
    if (opts.autoCreateLevels) params.append('autoCreateLevels', 'true');
    if (opts.selectAll) formData.append('selectAll', 'true');
    if (opts.filters) formData.append('filters', JSON.stringify(opts.filters));
    if (opts.search) formData.append('search', opts.search);
    const url = `${API_BASE}/courses/upload${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  uploadCoursesSmart: async (file, opts = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (opts.dryRun) params.append('dry_run', 'true');
    if (opts.overwriteExisting) params.append('overwriteExisting', 'true');
    const url = `${API_BASE}/courses/smart-upload${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  bulkUpdateCourses: async (file, opts = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (opts.selectedIds?.length) formData.append('selected_ids', JSON.stringify(opts.selectedIds));
    if (opts.selectAll) formData.append('selectAll', 'true');
    if (opts.filters) formData.append('filters', JSON.stringify(opts.filters));
    if (opts.search) formData.append('search', opts.search);

    const params = new URLSearchParams();
    params.append('bulkUpdate', 'true');
    if (opts.dryRun) params.append('dry_run', 'true');
    if (opts.autoCreateLevels) params.append('autoCreateLevels', 'true');
    const url = `${API_BASE}/courses/upload?${params.toString()}`;
    const res = await fetch(url, { method: 'POST', body: formData, credentials: 'include' });
    if (!res.ok) throw await errorFromResponse(res);
    return res.json();
  },
  bulkApplyCourseSections: (id, body = {}) => api(`/courses/${id}/bulk-apply-sections`, { method: 'POST', body }),


};

MastersService.getCachedCompactCourses = () => cachedCompactCourses;

MastersService.clearCoursesCache = () => {
  cachedCompactCourses = null;
  cachedCompactCoursesPromise = null;
};

MastersService.prefetchCompactCourses = async () => {
  if (Array.isArray(cachedCompactCourses)) return cachedCompactCourses;
  if (cachedCompactCoursesPromise) return cachedCompactCoursesPromise;

  cachedCompactCoursesPromise = (async () => {
    const res = await MastersService.listCourses({ compact: true });
    const data = Array.isArray(res?.data) ? res.data : [];
    cachedCompactCourses = data;
    return data;
  })().finally(() => {
    cachedCompactCoursesPromise = null;
  });

  return cachedCompactCoursesPromise;
};

export default MastersService;
